import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Card = styled(motion.div)`
  position: relative;
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  height: 300px;
  cursor: pointer;
  transition: all var(--transition-fast);
  
  @media (max-width: 768px) {
    height: 280px;
  }
  
  @media (max-width: 480px) {
    height: 250px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${props => props.accentColor || 'var(--primary)'};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform var(--transition-medium);
    z-index: 2;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-lg);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-medium);
  
  ${Card}:hover & {
    transform: scale(1.1);
  }
`;

const ProjectOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  opacity: 0.9;
  transition: all var(--transition-fast);
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
  
  ${Card}:hover & {
    opacity: 1;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 60%, rgba(0, 0, 0, 0.2) 100%);
  }
`;

const ProjectTitle = styled(motion.h3)`
  color: var(--white);
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  padding-bottom: 0.5rem;
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: ${props => props.accentColor || 'var(--secondary)'};
    transition: width var(--transition-fast);
  }
  
  ${Card}:hover &::after {
    width: 80px;
  }
`;

const ProjectCategory = styled(motion.span)`
  color: var(--white);
  font-size: 0.9rem;
  background-color: ${props => props.bgColor || 'var(--primary)'};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 0.8rem;
  transform-origin: left;
`;

const ProjectDescription = styled(motion.p)`
  color: var(--light);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all var(--transition-fast);
  max-height: 0;
  overflow: hidden;
  
  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
    max-height: 100px;
  }
`;

const ProjectCard = ({ project, index, onClick, categoryColors }) => {
  // Déterminer la couleur d'accent en fonction de la catégorie
  const getCategoryColor = () => {
    switch(project.category) {
      case 'site-vitrine':
        return categoryColors?.siteVitrine || 'var(--black)';
      case 'e-commerce':
        return categoryColors?.ecommerce || 'var(--secondary)';
      case 'application':
        return categoryColors?.application || 'var(--accent)';
      default:
        return 'var(--primary)';
    }
  };
  
  const getCategoryName = () => {
    switch(project.category) {
      case 'site-vitrine':
        return 'Site Vitrine';
      case 'e-commerce':
        return 'E-commerce';
      case 'application':
        return 'Application Web';
      default:
        return project.category;
    }
  };
  
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        delay: index * 0.1,
        ease: "easeOut"
      }
    }
  };
  
  const overlayVariants = {
    hover: {
      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, rgba(0, 0, 0, 0.3) 100%)'
    }
  };
  
  return (
    <Card
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      onClick={() => onClick(project)}
      accentColor={getCategoryColor()}
      whileHover="hover"
    >
      <ProjectImage src={project.image} alt={project.title} />
      <ProjectOverlay variants={overlayVariants}>
        <ProjectCategory 
          bgColor={getCategoryColor()}
          initial={{ scaleX: 0.8, opacity: 0.8 }}
          whileHover={{ scaleX: 1, opacity: 1 }}
        >
          {getCategoryName()}
        </ProjectCategory>
        <ProjectTitle 
          accentColor={getCategoryColor()}
          initial={{ y: 10, opacity: 0.8 }}
          whileHover={{ y: 0, opacity: 1 }}
        >
          {project.title}
        </ProjectTitle>
        <ProjectDescription>
          {project.description.substring(0, 80)}...
        </ProjectDescription>
      </ProjectOverlay>
    </Card>
  );
};

export default ProjectCard;
